import React, { useState, useEffect, useMemo } from 'react';
import { AgGridReact } from 'ag-grid-react';
import { makeStyles } from '@mui/styles';
import { Paper, Grid, Box, Button } from '@mui/material';
import { useAuthStore } from '../store/authStore';
import Controls from '../components/controls/Controls';
import * as MyCollections from "../services/MyCollections";
import { urlOverview } from '../config';
import { useThemeContext } from '../components/contexts/ThemeContext';

const useStyles = makeStyles(theme => ({
  pageContent: {
    marginTop: 20
  },
}))


function Overview() {
  const { roles } = useAuthStore();

  const token = useAuthStore().token;
  const contactId = useAuthStore().contactId;
  const [gridApi, setGridApi] = useState(null);
  const [tableData, setTableData] = useState([]);
  const [selectedRange, setSelectedRange] = useState("1");
  const classes = useStyles();
  const totalActive = tableData?.[0]?.activeClients;
  const { mode, toggleTheme } = useThemeContext();
  const darkTheme = mode === 'dark';

  // Each Column Definition results in one Column.
  const columnDefs = useMemo(() => [
    {
      headerName: "Rok",
      field: "year",
      lockPosition: 'left',
      minWidth: 80,
      maxWidth: 100,
      cellStyle: {
        backgroundColor: darkTheme ? '#333333' : '#f0f0f0',
        color: darkTheme ? 'white' : 'black',
        fontWeight: "bold"
      }
    },
    {
      headerName: "Miesiąc",
      field: "month",
      lockPosition: 'left',
      valueGetter: params => MyCollections.getMonthNameById(params.data.month.toString()),
      minWidth: 100,
      maxWidth: 120,
      cellStyle: {
        backgroundColor: darkTheme ? '#333333' : '#f0f0f0',
        color: darkTheme ? 'white' : 'black',
        fontWeight: "bold"
      }
    },
    {
      headerName: "Nowe umowy",
      field: "newContracts",
      minWidth: 100,
      lockPosition: 'left',
      cellStyle: {
        backgroundColor: darkTheme ? '#c99b00' : '#ffe599',
        color: darkTheme ? 'white' : 'black',
        fontWeight: "bold"
      }
    },
    {
      headerName: "Zakończone umowy",
      field: "endedContacts",
      minWidth: 100,
      lockPosition: 'left',
      cellStyle: {
        backgroundColor: darkTheme ? '#007baa' : '#00a8e6',
        color: darkTheme ? 'white' : 'black',
        fontWeight: "bold"
      }
    },
    {
      headerName: "Wynagrodzenia księgowych",
      field: "accountantsSalaries",
      minWidth: 100,
      lockPosition: 'left',
      valueFormatter: params =>
        new Intl.NumberFormat('pl-PL', { style: 'currency', currency: 'PLN' }).format(params.value),
      cellStyle: {
        backgroundColor: darkTheme ? '#6b4e90' : '#d9d2e9',
        color: darkTheme ? 'white' : 'black',
        fontWeight: "bold"
      }
    },
    {
      headerName: "Koszt księgowania per klient",
      field: "clientAccountingCost",
      minWidth: 100,
      lockPosition: 'left',
      valueFormatter: params =>
        new Intl.NumberFormat('pl-PL', { style: 'currency', currency: 'PLN' }).format(params.value),
      cellStyle: {
        backgroundColor: darkTheme ? '#8b4f4f' : '#e6b8af',
        color: darkTheme ? 'white' : 'black',
        fontWeight: "bold"
      }
    },
    {
      headerName: "Zatwierdzone koszty dodatkowe",
      field: "additionalCosts",
      minWidth: 100,
      lockPosition: 'left',
      valueFormatter: params =>
        new Intl.NumberFormat('pl-PL', { style: 'currency', currency: 'PLN' }).format(params.value),
      cellStyle: {
        backgroundColor: darkTheme ? '#2d5c2d' : '#c9f2c7', // zielonkawe tło
        color: darkTheme ? '#ffffff' : '#1b1b1b',           // kontrastujący tekst
        fontWeight: "bold"
      }
    }
  ], [darkTheme]);

  useEffect(() => {
    fetchElements(selectedRange);
  }, []);

  const onGridReady = (params) => {
    setGridApi(params)
  }

  const defaultColDef = {
    resizable: true,
    editable: false,
    filter: false,
    autoHeight: true,
    flex: 1
  }

  const handleRangeChange = (event) => {
    const selectedValue = event.target.value;
    setSelectedRange(selectedValue);
    fetchElements(selectedValue);
  };

  const fetchElements = async (range) => {
    let url = urlOverview + '/' + range;
    fetch(url, { headers: { 'Authorization': `Bearer ${token}` } })
      .then(response => response.json())
      .then(data => {
        const reversedData = data.reverse();
        setTableData(reversedData);
      })
      .catch(error => console.error(error));
  };

  return (
    <>
      <Paper className={classes.pageContent} elevation={0}>
        <Box 
          display="flex" 
          justifyContent="space-between" 
          alignItems="center" 
          flexWrap="wrap" 
          gap="20px" 
          sx={{ mb: 2, px: 2, pt: 2 }}
        >
          {/* Lewa strona: Filtry */}
          <Box display="flex" alignItems="center" flexWrap="wrap" gap={2}>
            <Box minWidth={250}>
              <Controls.MySelect
                name="yearId"
                label="Podaj zakres miesięcy: "
                value={selectedRange}
                onChange={handleRangeChange}
                options={MyCollections.getRangeCollection()}
              />
            </Box>
          </Box>

          {/* Prawa strona: Statystyki */}
          <Box display="flex" alignItems="center" flexWrap="wrap" gap="12px">
             <div className="stat-badges-row" style={{ margin: 0 }}>
               <span className="stat-badge stat-badge--green">
                 ✓ Aktywni klienci: {totalActive}
               </span>
             </div>
          </Box>
        </Box>

        <Box sx={{ m: '20px' }} />
        <Grid container rowSpacing={1} spacing={{ xs: 2, md: 4 }} columns={{ xs: 2, sm: 6, md: 12 }}>
          <Grid item xs={12}>
            <div className="App">
              <div className="tableData">
                {/* On div wrapping Grid a) specify theme CSS Class Class and b) sets Grid size */}
                <div
                  className={darkTheme ? 'ag-theme-balham-dark' : 'ag-theme-balham'}
                  style={{ marginLeft: 0, marginRight: 0, height: 650 }}
                >
                  <AgGridReact
                    key={darkTheme ? 'grid-dark' : 'grid-light'}
                    rowData={tableData}
                    columnDefs={columnDefs}
                    defaultColDef={defaultColDef}
                    onGridReady={onGridReady}
                    suppressBrowserResizeObserver={true}
                    animateRows={true}
                    rowSelection={false}
                    suppressSizeToFit={true}
                    localeText={MyCollections.localeTextPL}
                    tooltipShowDelay={0}
                    popupParent={document.body}
                    overlayLoadingTemplate={'<span class="ag-overlay-loading-center">Ładowanie danych...</span>'}
                    overlayNoRowsTemplate={'<span class="ag-overlay-loading-center">Ładowanie danych...</span>'}
                  />
                </div>
              </div>
            </div>
          </Grid>
        </Grid>
      </Paper>
    </>
  );
}

export default Overview;